import type { AxiosRequestConfig } from "axios";
import qs from "qs";
import { axiosInstance } from "../../axios-instance";
import { qsOptions } from "../qsOptions";
import type { R } from "../types";
import type {
  IChangeNotificationStatusRequest,
  IChangeNotificationStatusResponse,
  IDeleteNotificationRequest,
  IDeleteNotificationResponse,
  IGetNotificationsCountResponse,
  IGetNotificationsRequest,
  IGetNotificationsResponse,
  IGetUnreadNotificationsRequest,
  IGetUnreadNotificationsResponse,
  IStreamNotificationsEventData,
  IStreamNotificationsRequest,
  IStreamNotificationsResponse,
} from "./types";

interface INotificationsService {
  getAll: (
    p: IGetNotificationsRequest,
    c?: AxiosRequestConfig,
  ) => R<IGetNotificationsResponse>;
  getUnread: (
    p: IGetUnreadNotificationsRequest,
    c?: AxiosRequestConfig,
  ) => R<IGetUnreadNotificationsResponse>;
  getUnreadCount: (c?: AxiosRequestConfig) => R<IGetNotificationsCountResponse>;
  markAsRead: (
    p: IChangeNotificationStatusRequest,
    c?: AxiosRequestConfig,
  ) => R<IChangeNotificationStatusResponse>;
  markAllAsRead: (c?: AxiosRequestConfig) => R<void>;
  delete: (
    p: IDeleteNotificationRequest,
    c?: AxiosRequestConfig,
  ) => R<IDeleteNotificationResponse>;
  deleteAll: (c?: AxiosRequestConfig) => R<void>;
  stream: (p: IStreamNotificationsRequest) => IStreamNotificationsResponse;
}

export class NotificationsService implements INotificationsService {
  async getAll(
    p: IGetNotificationsRequest,
    c?: AxiosRequestConfig,
  ): R<IGetNotificationsResponse> {
    try {
      const params = qs.stringify(p, qsOptions);
      return axiosInstance.get(`/api/notifications?${params}`, c);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async getUnread(
    p: IGetUnreadNotificationsRequest,
    c?: AxiosRequestConfig,
  ): R<IGetUnreadNotificationsResponse> {
    try {
      const params = qs.stringify(p, qsOptions);
      return axiosInstance.get(
        `/api/notifications/unread/notifications?${params}`,
        c,
      );
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async getUnreadCount(
    c?: AxiosRequestConfig,
  ): R<IGetNotificationsCountResponse> {
    try {
      return axiosInstance.get(`/api/notifications/unread/count`, c);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async markAsRead(
    p: IChangeNotificationStatusRequest,
    c?: AxiosRequestConfig,
  ): R<IChangeNotificationStatusResponse> {
    try {
      return axiosInstance.put(`/api/notifications/mark/${p.id}`, undefined, c);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async markAllAsRead(c?: AxiosRequestConfig): R<void> {
    try {
      return axiosInstance.put(`/api/notifications/markAllRead`, undefined, c);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async delete(
    p: IDeleteNotificationRequest,
    c?: AxiosRequestConfig,
  ): R<IDeleteNotificationResponse> {
    try {
      return axiosInstance.delete(`/api/notifications/${p.id}`, c);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async deleteAll(c?: AxiosRequestConfig): R<void> {
    try {
      return axiosInstance.delete(`/api/notifications`, c);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /** Server-sent events over fetch: EventSource cannot send the Authorization header. */
  stream(p: IStreamNotificationsRequest): IStreamNotificationsResponse {
    const abortController = new AbortController();

    fetch(`${axiosInstance.defaults.baseURL}/api/notifications/stream`, {
      headers: {
        Accept: "text/event-stream",
        Authorization: `Bearer ${p.accessToken}`,
        "X-Tenant-Name": p.tenant,
      },
      signal: abortController.signal,
    })
      .then(async (response) => {
        if (!response.ok || !response.body) {
          throw new Error(
            `Failed to connect to notification stream: ${response.status}`,
          );
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        // Kept across chunks: an event's data line and its closing blank line can arrive apart
        let eventData = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split(/\r?\n/);
          buffer = lines.pop() ?? "";

          for (const line of lines) {
            if (line.startsWith("data:")) {
              eventData = line.slice(5).trim();
            } else if (line === "" && eventData) {
              try {
                const data: IStreamNotificationsEventData =
                  JSON.parse(eventData);
                p.onUnreadCount(data.unreadCount);
              } catch (e) {
                console.error("Failed to parse SSE data:", e);
              }
              eventData = "";
            }
          }
        }

        p.onClose?.();
      })
      .catch((error: Error) => {
        // fetch rejects with a DOMException "AbortError" when closed on purpose
        if (error.name !== "AbortError") {
          p.onClose?.(error);
        }
      });

    return {
      close: () => {
        abortController.abort();
      },
    };
  }
}
